import React, {useEffect} from 'react';
// util imports 
import {fetch_user_systems, fetch_public_systems} from './utils/systems/systemUtils';
import apiService, {validateSession} from './utils/apiService';
import { fetchUpcomingGames, fetchUserSubscription, fetchStandings } from './utils/apiRoutes';
import { useClearUserSession } from './hooks/user-hooks';
import { isToday } from './utils/general';
import { isSubActive } from './utils/user';
// redux imports
import { useSelector, useDispatch } from 'react-redux';
import { setMlbUpcoming, setMlbPublicSystems, setMlbStandings } from './redux/mlbSlice';
import { setNflPublicSystems, setNflUpcoming, setNflStandings } from './redux/nflSlice';
import { setNbaPublicSystems, setNbaUpcoming, setNbaStandings } from './redux/nbaSlice';
import { setPrivateSystems, setMatches, setFollowedSystems, setEmailSub, setSubscription } from './redux/userSlice';
import { setAppInfo } from './redux/applicationSlice';

// React Router
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

// Components
import NavBar from './components/NavBar';
import { Chart, Filler } from 'chart.js';

// CSS
import './assets/styles/master.scss';

const App = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const clearSession = useClearUserSession();
    Chart.register(Filler); // allows for the chart js graphs to have a filler
    const dispatch = useDispatch();
    const upcoming = useSelector((state) => state.mlb.upcoming);
    const user_id = useSelector((state) => state.user.id);
    const loggedIn = useSelector((state) => state.user.loggedIn);
    const appInfo = useSelector(state=> state.app.info);

    // validate session on initial render
    useEffect(() => {
        const validate = async () => {
            try {
                const isValid = await validateSession();
                if (!isValid) {
                    clearSession();
                    navigate('/');
                }
            } catch(error) {
                console.error('Session validation failed:', error.message);
                clearSession();
                navigate('/');
            }
        };
        validate();
    }, [navigate, clearSession]);

    // function to get system matches
    const fetch_sysMatches = async(ids) => {
        if (!ids || ids.length === 0) {
            console.warn('No system IDs provided to fetch matches');
            return;
        }

        const body = { ids };
        
        try {
            const payload = await apiService('systems/upcoming_matches', "POST", body);
            if (payload) {
                dispatch(setMatches(payload));
            }
        } catch(error) {
            // Do nothing for 304 status
            if(error.status === 304) {
                return;
            }

            // Handle all other errors
            if(error.status === 401 || error.message === 'Session Expired') {
                clearSession();
                navigate('/');
            } else if(error.status === 0) {
                console.error('Network error while fetching system matches:', error.message);
            } else {
                console.error(`Error fetching system matches (${error.status}):`, error.message);
            }
        }
    };

    // get public systems for all sports
    const getPublicSystems = async(lastId=null) => {
        try {
            // Fetch all public systems in parallel
            const [mlbResponse, nflResponse, nbaResponse] = await Promise.all([
                fetch_public_systems("mlb", lastId),
                fetch_public_systems("nfl", lastId),
                fetch_public_systems("nba", lastId)
            ]);

            // Check if any requests failed
            if (!mlbResponse || !nflResponse || !nbaResponse) {
                console.error('Failed to fetch some public systems');
                return;
            }

            // Update Redux store with responses
            dispatch(setMlbPublicSystems(mlbResponse));
            dispatch(setNflPublicSystems(nflResponse));
            dispatch(setNbaPublicSystems(nbaResponse));
        } catch(error) {
            if(error.status === 401 || error.message === 'Session Expired') {
                clearSession();
                navigate('/');
            } else if(error.status === 0) {
                console.error('Network error while fetching public systems:', error.message);
            } else {
                console.error(`Error fetching public systems (${error.status}):`, error.message);
            }
        }
    };

    const getStandings = async() => {
        try {
            const [mlbResponse, nflResponse, nbaResponse] = await Promise.all([
                fetchStandings('mlb'),
                fetchStandings('nfl'),
                fetchStandings('nba')
            ]);

            dispatch(setMlbStandings(mlbResponse));
            dispatch(setNflStandings(nflResponse));
            dispatch(setNbaStandings(nbaResponse));
        } catch(error) {
            if(error.status === 401 || error.message === 'Session Expired') {
                clearSession();
                navigate('/');
            } else if(error.status === 0) {
                console.error('Network error while fetching sport standings:', error.message);
            } else {
                console.error(`Error fetching public systems (${error.status}):`, error.message);
            }
        }
    };

    // run when user changes, set state and fetch systems, system matches, and check for email noti subs & subscription
    useEffect(() => {
        if(user_id !== undefined && user_id !== null) {
            const await_fetch = async () => {
                try {
                    // Fetch systems and public systems in parallel
                    const [systems, emailSub, userSub] = await Promise.all([
                        fetch_user_systems(user_id, clearSession, navigate),
                        apiService('app/email-sub-status'),
                        fetchUserSubscription(user_id)
                    ]);

                    // Update systems in Redux store
                    if (systems) {
                        dispatch(setPrivateSystems(systems.privateSystems));
                        dispatch(setFollowedSystems(systems.followedSystems));
                        
                        // Fetch public systems
                        await getPublicSystems();

                        // Prepare system IDs for matches
                        const privSysIds = systems.privateSystems.map(sys => sys._id);
                        const pubIds = Object.values(systems.followedSystems)
                            .flat()
                            .map(sys => sys._id);
                        
                        // Fetch matches for all systems
                        await fetch_sysMatches([...privSysIds, ...pubIds]);
                    }

                    // Update email subscription status
                    if (emailSub) {
                        dispatch(setEmailSub(emailSub.email_sub));
                    }

                    // Update user subscription status
                    if (userSub) {
                        const { isActive } = isSubActive(userSub);
                        dispatch(setSubscription({ subscription: userSub, isActive }));
                    }
                } catch(error) {
                    // Do nothing for 304 status
                    if(error.status === 304) {
                        return;
                    }

                    if(error.status === 401 || error.message === 'Session Expired') {
                        clearSession();
                        navigate('/');
                    } else if(error.status === 0) {
                        console.error('Network error while fetching user data:', error.message);
                    } else {
                        console.error(`Error fetching user data (${error.status}):`, error.message);
                    }
                }
            };
            await_fetch();
        }
    }, [user_id, clearSession, navigate]);

    useEffect(() => {
        if(user_id !== undefined && user_id !== null) {
            getStandings();
        }
    }, [user_id]);

    // fetch upcoming games if user is logged in
    useEffect(() => { 
        const checkGames = async () => {
            // make sure user is logged in
            if(loggedIn) {
                // if last updated is not today (or upcoming is null - fetch upcoming
                if(!upcoming || !isToday(new Date(appInfo?.upcoming_timestamp))) {
                    try {
                        // Fetch all games in parallel for better performance
                        const [mlbResponse, nflResponse, nbaResponse] = await Promise.all([
                            fetchUpcomingGames('mlb/upcoming'),
                            fetchUpcomingGames('nfl/week/games?week=current'),
                            fetchUpcomingGames('nba/upcoming')
                        ]);

                        // Check if any requests failed
                        if (mlbResponse === null || nflResponse === null || nbaResponse === null) {
                            console.error('Failed to fetch some game data');
                            return;
                        }

                        let tmpAppInfo = { ...appInfo, upcoming_timestamp: Date.now() };
                        
                        dispatch(setMlbUpcoming(mlbResponse));
                        dispatch(setNflUpcoming(nflResponse));
                        dispatch(setNbaUpcoming(nbaResponse));
                        dispatch(setAppInfo(tmpAppInfo));   
                    } catch(error) {
                        // Handle authentication errors
                        if(error.status === 401 || error.message === 'Session Expired') {
                            clearSession();
                            navigate('/');
                        } else if(error.status === 0) {
                            // Handle network errors
                            console.error('Network error while fetching games:', error.message);
                        } else {
                            // Handle other server errors
                            console.error(`Error fetching games (${error.status}):`, error.message);
                        }
                    }
                }
            }
        };
        checkGames();
    }, [loggedIn, upcoming, appInfo, clearSession, navigate]);

    // Protected route check
    useEffect(() => {
        const protectedRoutes = ['/profile', '/system-builder', '/systems', '/system', '/subscribed', '/upcoming-games', '/game-analyzer'];
        const isProtectedRoute = protectedRoutes.some(route => location.pathname.startsWith(route));
        
        if (!loggedIn && isProtectedRoute) {
            navigate('/');
        }
    }, [loggedIn, location, navigate]);

    return (
        <div className="flex flex-col min-h-screen">
            <NavBar />
            <main className="flex-grow bg-white dark:bg-brand-dark-900 text-gray-900 dark:text-white transition-colors duration-300">
                <Outlet />
            </main>
        </div>
    );
};

export default App;